/**
 * Kẽm in CTP (31/10/2026): kẽm luôn xuất mới cho mỗi lệnh sản xuất, dùng 1 lần, không tái sử dụng;
 * số lá theo kiểu in. In gia công ngoài thì bên ngoài ra kẽm, công ty không xuất kẽm, không trừ kho kẽm.
 */
export interface PlatePrintPreset {
  key: string;
  label: string;
  /** Màu từng lá kẽm (số lá = độ dài) */
  colors: string[];
}

const CMYK = ['Cyan (C)', 'Magenta (M)', 'Yellow (Y)', 'Black (K)'];

export const PLATE_PRINT_PRESETS: PlatePrintPreset[] = [
  { key: 'CMYK', label: 'In 4 màu CMYK', colors: CMYK },
  { key: 'CMYK_2SPOT', label: 'In CMYK + 2 màu pha', colors: [...CMYK, 'Pha 1', 'Pha 2'] },
  { key: 'CMYK_AB', label: 'In CMYK 2 mặt (A/B)', colors: [...CMYK.map((c) => `${c} - Mặt A`), ...CMYK.map((c) => `${c} - Mặt B`)] },
  { key: 'CMYK_WORK_TURN', label: 'In tự trở CMYK', colors: CMYK.map((c) => `${c} - Tự trở`) },
  { key: 'TWO_COLOR', label: 'In 2 màu (vd CM, YK)', colors: ['Màu 1', 'Màu 2'] },
];

/** Số lá kẽm của một kiểu in (không rõ kiểu thì 4 lá CMYK) */
export function platesForPreset(key: string | null | undefined): number {
  return (PLATE_PRINT_PRESETS.find((p) => p.key === key) || PLATE_PRINT_PRESETS[0]).colors.length;
}

/** Dòng kẽm thuê ngoài: bên in gia công ra kẽm, công ty không ghi bản, không trừ kho kẽm */
export function isOutsourcedPlate(t: { plateSource?: string | null; source?: string | null } | null | undefined): boolean {
  return t?.plateSource === 'OUTSOURCE' || t?.source === 'OUTSOURCE';
}

/** Khổ kẽm làm loại hàng của kho kẽm (gộp khoảng trắng, chữ thường "mm") */
export function plateStockType(size: string | null | undefined): string {
  const s = String(size || '').trim().replace(/\s*[x×]\s*/gi, 'x').replace(/\s+/g, ' ');
  if (!s) return 'Chưa rõ khổ';
  return /mm$/i.test(s) ? s.replace(/\s*mm$/i, ' mm') : `${s} mm`;
}

export interface CtpLedgerRow {
  id: string;
  plateType: string | null;
  importQty: number;
  usedQty: number;
  stockQty: number;
}

/**
 * Tồn kho kẽm theo từng khổ = cộng dồn nhập − xuất theo thứ tự thời gian (sổ đã sắp sẵn).
 * Trả các dòng cần ghi lại tồn.
 */
export function computeCtpStock(rows: CtpLedgerRow[]): { id: string; stockQty: number }[] {
  const balance = new Map<string, number>();
  const changes: { id: string; stockQty: number }[] = [];
  for (const r of rows) {
    const key = r.plateType || '';
    const next = (balance.get(key) || 0) + (Number(r.importQty) || 0) - (Number(r.usedQty) || 0);
    balance.set(key, next);
    if (next !== r.stockQty) changes.push({ id: r.id, stockQty: next });
  }
  return changes;
}

/** Kẽm in xong giữ 24 giờ để in bù (kẽm bay màu, không lưu lâu) */
export const PLATE_REPRINT_HOLD_MS = 24 * 60 * 60 * 1000;

/** Lệnh đã đóng bài in: không còn in bù */
export const CLOSED_ORDER_STATUSES = ['MANUFACTURED', 'COMPLETED', 'CLOSED', 'CANCELLED'];

/** Bộ kẽm hết hạn giữ: in xong quá 24 giờ và mọi lệnh dùng bộ kẽm đã đóng → thanh lý */
export function isPlateHoldExpired(
  printFinishedAt: Date | string | null | undefined,
  orderStatuses: (string | null | undefined)[],
  now: Date = new Date(),
): boolean {
  if (!printFinishedAt) return false;
  const t = new Date(printFinishedAt).getTime();
  if (!Number.isFinite(t) || now.getTime() - t < PLATE_REPRINT_HOLD_MS) return false;
  return orderStatuses.length > 0 && orderStatuses.every((s) => CLOSED_ORDER_STATUSES.includes(String(s)));
}

/** Khổ kẽm của máy in đã chuẩn hoá (máy chưa khai báo → null). Mỗi máy in dùng đúng 1 khổ kẽm. */
export function pressPlateSize(machine: { plateSize?: string | null } | null | undefined): string | null {
  const raw = String(machine?.plateSize || '').trim();
  return raw ? plateStockType(raw) : null;
}

/** Bộ kẽm chỉ dùng cho máy in cùng khổ (không dùng khổ của máy này cho máy kia) */
export function sameReadyPlateSize(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!String(a || '').trim() || !String(b || '').trim()) return false;
  return plateStockType(a) === plateStockType(b);
}

/**
 * Nhãn lệnh sản xuất trong thông báo: mã đã bắt đầu bằng "LSX" thì giữ nguyên, không thì thêm "LSX "
 * (trước đây ghi `LSX ${orderId}` → hiện "LSX LSX-2610-059" — FB-SN0CON).
 */
export function lsxLabel(orderId: string | null | undefined): string {
  const code = String(orderId ?? '').trim();
  if (!code) return 'LSX';
  return /^lsx/i.test(code) ? code : `LSX ${code}`;
}
